import { usePlayerDiagnostics } from "@components/SwipeList/Slot.diagnostics";
import LBox from "@components/LBox";
import { SwipeListComponentProps } from "@components/SwipeList/types";
import type { QueueItem } from "@utils/player/utils/queue.types";
import {
  ComponentType,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useAnimatedReaction,
  SharedValue,
  useSharedValue,
} from "react-native-reanimated";

interface SlotProps {
  activeIndex: number | undefined;
  Component: ComponentType<SwipeListComponentProps>;
  disabled?: boolean;
  index: number;
  items: QueueItem[];
  queueType: "foryou" | "playlist";
  surface?: "feed" | "bar" | "unknown";
  progress: SharedValue<number>;
  isGestureActive: SharedValue<boolean>;
  height: number;
  baseHeight: number;
  numberOfSlots: number;
}

const Slot = ({
  activeIndex,
  Component,
  disabled,
  index,
  items,
  queueType,
  surface = "unknown",
  progress,
  isGestureActive,
  height,
  baseHeight,
  numberOfSlots,
}: SlotProps) => {
  const { captureBlankSwipeListSlot } = usePlayerDiagnostics();

  const prevIndexSV = useSharedValue(-1);
  const authoritativeActiveIndexRef = useRef(activeIndex ?? -1);

  const style = useAnimatedStyle(() => {
    const basePosition = index * baseHeight;
    const moveThreshold = (index + Math.floor(numberOfSlots / 2)) * baseHeight;
    const nextPosition = basePosition + numberOfSlots * baseHeight;

    const clampedProgress = Math.max(0, progress.value);
    const cycleHeight = numberOfSlots * baseHeight;
    const effectiveProgress =
      clampedProgress - Math.floor(clampedProgress / cycleHeight) * cycleHeight;

    // Show item when it's in its current position or next position
    const opacity =
      effectiveProgress < moveThreshold ||
      effectiveProgress >= moveThreshold + baseHeight
        ? 1
        : 0;

    let finalPosition = basePosition;
    finalPosition += Math.floor(clampedProgress / cycleHeight) * cycleHeight;

    if (
      effectiveProgress >= moveThreshold &&
      effectiveProgress < moveThreshold + baseHeight
    ) {
      const progressInWindow = effectiveProgress - moveThreshold;
      const animationProgress = progressInWindow / baseHeight;
      finalPosition += (nextPosition - basePosition) * animationProgress;
    } else if (effectiveProgress >= moveThreshold + baseHeight) {
      finalPosition += nextPosition - basePosition;
    }

    // Scale the position based on the ratio between actual height and base height
    finalPosition = (finalPosition * height) / baseHeight;

    return {
      transform: [{ translateY: finalPosition }],
      opacity,
      height: height,
    };
  });

  const itemIndex = useDerivedValue(() => {
    const clampedOffset = Math.max(0, progress.value);
    const slotOffset = clampedOffset - index * baseHeight; // Offset relative to the slot
    const cycleHeight = numberOfSlots * baseHeight;
    const effectiveSlotOffset =
      slotOffset - Math.floor(slotOffset / cycleHeight) * cycleHeight;

    let currentIndex =
      Math.floor(slotOffset / cycleHeight) * numberOfSlots + index;

    if (effectiveSlotOffset >= 2 * baseHeight) {
      currentIndex += numberOfSlots;
    }

    return currentIndex;
  });

  const [currentItem, setCurrentItem] = useState<QueueItem | null>(
    () => items[itemIndex.get()] ?? null,
  );
  const [prevItems, setPrevItems] = useState(items);
  const [prevActiveIndex, setPrevActiveIndex] = useState(activeIndex);

  useLayoutEffect(() => {
    authoritativeActiveIndexRef.current = activeIndex ?? -1;
  }, [activeIndex]);

  // Keep queue switches and authoritative index changes deterministic even if
  // the UI-thread animated reaction misses a value on web. Gesture-driven
  // changes still flow through useAnimatedReaction below.
  if (items !== prevItems || activeIndex !== prevActiveIndex) {
    // Reanimated's derived index can still reflect the previous queue during
    // the React render that replaces `items` or advances the active index
    // (especially on web). Resolve the slot from the queue's authoritative
    // active index instead so the visible slot cannot retain old metadata while
    // the audio player has already moved to the new track.
    const nextItemIndex =
      activeIndex === undefined
        ? itemIndex.get()
        : getSlotItemIndex(activeIndex, index, numberOfSlots);
    const nextItem = items[nextItemIndex];
    setPrevItems(items);
    setPrevActiveIndex(activeIndex);
    if (nextItem && nextItem !== currentItem) {
      setCurrentItem(nextItem);
      prevIndexSV.set(nextItemIndex);
    }
  }

  const commitAnimatedItem = useCallback(
    (value: number, nextItem: QueueItem) => {
      const authoritativeActiveIndex = authoritativeActiveIndexRef.current;

      if (
        authoritativeActiveIndex >= 0 &&
        !isGestureActive.get() &&
        value !==
          getSlotItemIndex(authoritativeActiveIndex, index, numberOfSlots)
      ) {
        return;
      }

      setCurrentItem(nextItem);
    },
    [index, isGestureActive, numberOfSlots],
  );

  // Only update React state when index actually changes (prevents re-renders during gestures)
  // This dramatically reduces React re-renders during swipe gestures
  useAnimatedReaction(
    () => itemIndex.value,
    (value) => {
      // Keep comparison + state on UI thread (avoid JS refs in worklets).
      if (value === prevIndexSV.value) return;
      prevIndexSV.set(value);

      // Avoid setting null (causes unmount/remount churn). If missing, keep prior item.
      const nextItem = items[value];
      if (!nextItem) return;

      runOnJS(commitAnimatedItem)(value, nextItem);
    },
    [commitAnimatedItem, items], // Dependency on items ensures reaction updates if queue switches
  );

  useEffect(() => {
    if (index !== 0 || currentItem || items.length === 0) return;

    const timeout = setTimeout(() => {
      const expectedIndex = itemIndex.get();
      const expectedItem = items[expectedIndex];

      captureBlankSwipeListSlot({
        expectedIndex,
        expectedItem,
        itemCount: items.length,
        queueType,
        slotIndex: index,
        surface,
      });
    }, 1_000);

    return () => clearTimeout(timeout);
  }, [currentItem, index, itemIndex, items, queueType, surface]);

  if (!currentItem) return null;

  return (
    <LBox
      style={[
        {
          position: "absolute",
        },
        style,
      ]}
    >
      <Component disabled={disabled} item={currentItem} />
    </LBox>
  );
};

export default Slot;

export const getSlotItemIndex = (
  activeIndex: number,
  slotIndex: number,
  numberOfSlots: number,
) => {
  const slotOffset = activeIndex - slotIndex;
  const cycle = Math.floor(slotOffset / numberOfSlots);
  const effectiveSlotOffset = slotOffset - cycle * numberOfSlots;
  let itemIndex = cycle * numberOfSlots + slotIndex;

  if (effectiveSlotOffset >= 2) {
    itemIndex += numberOfSlots;
  }

  return itemIndex;
};
